import React, { useState } from 'react';
import {
  Container,
  VStack,
  HStack,
  Box,
  Heading,
  Text,
  Button,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Card,
  CardBody,
  Icon,
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  useSteps,
} from '@chakra-ui/react';
import { FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import PostCreationForm from '../components/PostCreationForm';
import { Post } from '../types';

interface ImageFile {
  id: string;
  file: File;
  preview: string;
  name: string;
  size: number;
}

interface PostFormData {
  title: string;
  description: string;
  categoryId: string;
  brandId: string;
  condition: string;
  price: number;
  location: string;
  tags: string;
}

const CreatePostPage: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [createdPostId, setCreatedPostId] = useState<string | null>(null);
  
  const toast = useToast();
  const navigate = useNavigate();

  const steps = [
    { title: 'Basic Info', description: 'Title, description, and price' },
    { title: 'Details', description: 'Category, brand, and condition' },
    { title: 'Photos', description: 'Upload item images' },
    { title: 'Review', description: 'Review and publish' },
  ];

  const { activeStep } = useSteps({
    index: 0,
    count: steps.length,
  });

  const handleSubmit = async (formData: PostFormData, images: ImageFile[]) => {
    setIsSubmitting(true);

    try {
      // Simulate form submission delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Simulate creating a new post
      const newPost: Post = {
        id: Math.random().toString(36).substr(2, 9), // Generate random ID
        title: formData.title,
        description: formData.description,
        price: formData.price,
        condition: formData.condition as 'new' | 'like-new' | 'excellent' | 'good' | 'fair' | 'poor',
        images: images.map(imageFile => imageFile.preview), // Use preview URLs
        categoryId: formData.categoryId,
        brandId: formData.brandId,
        sellerId: '1', // Mock current user ID
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        location: formData.location,
      };

      // In a real app, this would be an API call
      console.log('Creating new post:', newPost);

      setCreatedPostId(newPost.id);
      setSubmissionSuccess(true);

      toast({
        title: 'Post created successfully!',
        description: 'Your item is now live on the marketplace.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

    } catch (error) {
      toast({
        title: 'Error creating post',
        description: 'There was an error creating your post. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleViewPost = () => {
    if (createdPostId) {
      navigate(`/post/${createdPostId}`);
    }
  };

  const handleCreateAnother = () => {
    setSubmissionSuccess(false);
    setCreatedPostId(null);
    window.location.reload(); // Reset the form
  };

  if (submissionSuccess) {
    return (
      <Container maxW="4xl" py={8}>
        <VStack spacing={8} align="center">
          <Icon as={FaCheckCircle} boxSize={16} color="green.500" />
          
          <VStack spacing={4} textAlign="center">
            <Heading size="xl" color="brand.green.dark">
              Post Created Successfully!
            </Heading>
            <Text fontSize="lg" color="gray.600" maxW="md">
              Your item has been posted to the marketplace and is now visible to potential buyers.
            </Text>
          </VStack>

          <Alert status="success" borderRadius="md" maxW="md">
            <AlertIcon />
            <Box>
              <AlertTitle>What happens next?</AlertTitle>
              <AlertDescription>
                Buyers can now see your listing and contact you through the messaging system. 
                You'll receive notifications for any messages or inquiries.
              </AlertDescription>
            </Box>
          </Alert>

          <HStack spacing={4}>
            <Button
              colorScheme="green"
              size="lg"
              onClick={handleViewPost}
            >
              View My Post
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={handleCreateAnother}
            >
              Create Another Post
            </Button>
            <Button
              variant="ghost"
              size="lg"
              onClick={() => navigate('/profile')}
            >
              Go to My Profile
            </Button>
          </HStack>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="6xl" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <HStack spacing={4} align="center">
          <Button
            variant="ghost"
            leftIcon={<FaArrowLeft />}
            onClick={handleGoBack}
          >
            Back
          </Button>
          <Box flex={1}>
            <Heading size="xl" color="brand.green.dark">
              Create New Listing
            </Heading>
            <Text color="gray.600" fontSize="lg">
              Post your tennis equipment for sale to reach verified buyers
            </Text>
          </Box>
        </HStack>

        {/* Progress Stepper */}
        <Card>
          <CardBody>
            <Stepper index={activeStep} orientation="horizontal" height="80px" gap="0">
              {steps.map((step, index) => (
                <Step key={index}>
                  <StepIndicator>
                    <StepStatus
                      complete={<StepIcon />}
                      incomplete={<StepNumber />}
                      active={<StepNumber />}
                    />
                  </StepIndicator>

                  <Box flexShrink="0">
                    <StepTitle>{step.title}</StepTitle>
                    <StepDescription>{step.description}</StepDescription>
                  </Box>

                  <StepSeparator />
                </Step>
              ))}
            </Stepper>
          </CardBody>
        </Card>

        {/* Tips and Guidelines */}
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Tips for a successful listing:</AlertTitle>
            <AlertDescription>
              <VStack align="start" spacing={1} mt={2}>
                <Text>• Use clear, well-lit photos from multiple angles</Text>
                <Text>• Write detailed descriptions including size, condition, and any defects</Text>
                <Text>• Research similar items to price competitively</Text>
                <Text>• Be honest about the condition to build trust with buyers</Text>
              </VStack>
            </AlertDescription>
          </Box>
        </Alert>

        {/* Form */}
        <Card>
          <CardBody>
            <PostCreationForm
              onSubmit={handleSubmit}
              isLoading={isSubmitting}
            />
          </CardBody>
        </Card>

        {/* Additional Information */}
        <Card>
          <CardBody>
            <VStack spacing={4} align="start">
              <Heading size="md">Important Information</Heading>
              <VStack align="start" spacing={2}>
                <Text fontSize="sm" color="gray.600">
                  <strong>Listing Duration:</strong> Your post will remain active until you mark it as sold or withdraw it.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  <strong>Payment:</strong> All transactions are handled directly between buyer and seller. ReDeuce provides the platform for communication.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  <strong>Safety:</strong> Always meet in public places and trust your instincts when meeting buyers.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  <strong>Support:</strong> Contact our support team if you have any questions or concerns about your listing.
                </Text>
              </VStack>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
};

export default CreatePostPage; 